import {
    Component,
    EventEmitter,
    inject,
    Input,
    Output
} from '@angular/core';

import { DomSanitizer } from "@angular/platform-browser";

import {
    distinctUntilChanged,
    map,
    of,
    startWith,
    switchMap,
    withLatestFrom
} from 'rxjs';

import {mobileCheck} from 'src/utilities';

import {
    ConfigurationManagerService,
    ConnectionService,
    ThemeModeService,
    ToolbarEventsService
} from 'src/app/core';

import {OpenDialogsService} from 'src/app/dialogs';
import { subscribeInComponent } from 'src/app/shared/angular-utils';
import { ActiveSaleStoreService } from 'src/app/features';
import { $state } from 'app/ajs-upgraded-providers';
import { DualService } from 'src/app/dual.service';
import { ToolbarLayoutService } from 'src/app/shared/services/toolbar-layout.service';

@Component({
	standalone: false,
    selector: 'app-left-toolbar',
    templateUrl: './left-toolbar.component.html',
    styleUrls: ['./left-toolbar.component.scss'],
    host: {class: 'tw-flex tw-items-center'}
})
export class LeftToolbarComponent {
    private readonly connectionService = inject(ConnectionService);
    private readonly domSanitizer = inject(DomSanitizer);
    private readonly openDialogsService = inject(OpenDialogsService);
    private readonly toolbarEventsService = inject(ToolbarEventsService);
    protected readonly themeModeService = inject(ThemeModeService);
    protected readonly configurationManagerService = inject(ConfigurationManagerService);
    protected readonly state = inject($state);
    protected readonly dualService = inject(DualService);
    private readonly toolbarLayoutService = inject(ToolbarLayoutService);

    protected isOffline = this.connectionService.isOffline;
    protected isTableNameOnTopBar : boolean = this.configurationManagerService.getPreference('cashregister.table_name_on_topbar') || false;

    @Input() menuIcon = false;
    @Output() menuClickChange = new EventEmitter<void>();

    protected isMobile = mobileCheck();
    protected logoLight = this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/tilby/Tilby_a.svg');
    protected logoDark = this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/tilby/Tilby_a-dark.svg');
    protected moduleTitle?: string;
    protected regularTitle: boolean = true;
    protected saleUpdates$ = ActiveSaleStoreService.saleUpdates$;
    protected currentModule = this.state.current;

    protected showTitle$ = this.toolbarLayoutService.showModuleTitle$;

    constructor(
    ) {
        subscribeInComponent(this.toolbarEventsService.moduleTitle.pipe(
            withLatestFrom(this.dualService.nextRoute$),
            switchMap(([moduleTitle, nextRoute]) => {
                const defaultTitle = `TOPBAR.TITLE.${moduleTitle}`;
                const nextRouteDifferent = !!nextRoute && !nextRoute.includes('cashregister');

                if (!this.isTableNameOnTopBar || nextRouteDifferent) {
                    return of(defaultTitle);
                }

                return this.saleUpdates$.pipe(
                    map(data => this.getSaleTitle(data.currentSale, defaultTitle)),
                    startWith(defaultTitle),
                    distinctUntilChanged()
                );
            })
        ), title => {
            if (title !== undefined) {
                this.moduleTitle = title;
            }
        });
    }

    /** Returns the title for the current sale, or undefined to keep the title already shown. */
    private getSaleTitle(currentSale: any, defaultTitle: string): string | undefined {
        if (!currentSale) {
            return defaultTitle;
        }

        const formatTime = (date: string) => `${new Date(date).getHours()}:${new Date(date).getMinutes().toString().padStart(2, '0')}`;

        switch (currentSale.order_type) {
            case 'normal':
                return currentSale.room_name && currentSale.table_name
                    ? currentSale.room_name + " - " + currentSale.table_name
                    : "TOPBAR.TITLE.CASHREGISTER";
            case 'take_away':
                return currentSale.deliver_at ? 'Asporto' + " - " + formatTime(currentSale.deliver_at) : undefined;
            case 'delivery': {
                const deliver_at = currentSale.deliver_at ? formatTime(currentSale.deliver_at) : undefined;
                return (currentSale.channel ? currentSale.channel[0].toUpperCase() + currentSale.channel.slice(1) : 'Delivery') + ", " + deliver_at + (currentSale.external_id ? " - " + currentSale.external_id.slice(0, 3) + "..." : "");
            }
            default:
                return "";
        }
    }

    menuClick($event: MouseEvent) {
        $event.stopPropagation();
        this.menuClickChange.emit();
    }

    openOfflineDialog() {
        return this.openDialogsService.openOfflineDialog();
    }
}
